/**
 * arbi doctags create [tag-id] [doc-ids...]  — assign tag to docs (pickers if no args)
 * arbi doctags delete [tag-id] [doc-ids...]  — remove tag from docs (pickers if no args)
 * arbi doctags generate                      — AI-generate doctags (pickers if no flags)
 */

import { Command } from 'commander'
import { doctags as doctagOps, tags as tagOps, documents } from '@arbidocs/sdk'
import {
  runAction,
  resolveWorkspace,
  requireInteractive,
  resolveByIdOrName,
  failResolveAndExit,
  pickFromList,
  dryRun,
} from '../helpers.js'
import { promptCheckbox } from '../prompts.js'
import { success } from '../output.js'

/**
 * Resolve a tag arg (id like `tag-…` or a unique tag name) to its external_id.
 * Delegates to the shared `resolveByIdOrName` so the ambiguous/not-found
 * messages match every other resolver in the CLI.
 */
async function resolveTagRef(
  arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi'],
  ref: string
): Promise<string> {
  if (ref.startsWith('tag-')) return ref
  const data = await tagOps.listTags(arbi)
  const res = resolveByIdOrName(data as Array<{ external_id?: string; name?: string }>, ref)
  if (!res.ok) failResolveAndExit('tag', ref, res)
  return res.id
}

/** Interactive single-tag picker. Delegates the empty/non-TTY handling to
 *  `pickFromList` so this stays a one-liner over the formatting concern. */
function pickTag(
  arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi'],
  message = 'Select tag'
): Promise<string> {
  return tagOps.listTags(arbi).then((data) =>
    pickFromList(
      data,
      (t) => ({
        name: `${t.name} (${(t.tag_type as { type?: string })?.type ?? ''})`,
        value: t.external_id as string,
        description: t.external_id as string,
      }),
      {
        message,
        emptyMessage: 'No tags found.',
        nonTtyHint: 'Pass <tag-id> (or unique tag name) and <doc-ids...> directly.',
      }
    )
  )
}

/**
 * Multi-select doc picker. Stays separate from `pickFromList` (which is
 * single-select) because doctag operations inherently target a set.
 */
async function pickDocs(
  arbi: Awaited<ReturnType<typeof resolveWorkspace>>['arbi'],
  message = 'Select documents'
): Promise<string[]> {
  const data = await documents.listDocuments(arbi)
  if (data.length === 0) {
    process.stderr.write('No documents found.\n')
    process.exit(0)
  }
  requireInteractive('Pass <doc-ids...> directly: arbi docs --ids to list them.')
  return promptCheckbox(
    message,
    data.map((d) => ({
      name: `${d.file_name ?? 'Unnamed'} (${d.status})`,
      value: d.external_id as string,
    }))
  )
}

export function registerDoctagsCommand(program: Command): void {
  const doctagsCmd = program
    .command('doctags')
    .description('Doctags (tag↔doc links): create, delete, generate')

  doctagsCmd
    .command('create [tag] [doc-ids...]')
    .description('Assign a tag to documents (tag accepts ID or unique name; pickers if no args)')
    .option('-n, --note <text>', 'Note for the doctag')
    .action((tagRef?: string, docIds?: string[], opts?: { note?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()
        let tagId = tagRef ? await resolveTagRef(arbi, tagRef) : undefined
        if (!tagId) tagId = await pickTag(arbi, 'Select tag to assign')
        if (!docIds || docIds.length === 0) docIds = await pickDocs(arbi, 'Select documents to tag')
        if (docIds.length === 0) return
        const data = await doctagOps.assignDocTags(arbi, tagId, docIds, opts?.note)
        success(`Created ${data.length} doctag(s) for tag ${tagId}.`)
      })()
    )

  doctagsCmd
    .command('delete [tag] [doc-ids...]')
    .description('Remove a tag from documents (tag accepts ID or unique name; pickers if no args)')
    .option('--dry-run', 'Preview which doctag links would be removed (no SDK call)')
    .action(
      (tagRef: string | undefined, docIds: string[] | undefined, opts: { dryRun?: boolean }) =>
        runAction(async () => {
          const { arbi } = await resolveWorkspace()
          let tagId = tagRef ? await resolveTagRef(arbi, tagRef) : undefined
          if (!tagId) tagId = await pickTag(arbi, 'Select tag to remove')
          if (!docIds || docIds.length === 0)
            docIds = await pickDocs(arbi, 'Select documents to untag')
          if (docIds.length === 0) return
          if (opts?.dryRun) {
            dryRun(`remove tag ${tagId} from ${docIds.length} document(s)`, docIds)
            return
          }
          await doctagOps.removeDocTags(arbi, tagId, docIds)
          success(`Removed tag ${tagId} from ${docIds.length} document(s).`)
        })()
    )

  doctagsCmd
    .command('generate')
    .description('AI-generate doctags (interactive pickers if no flags)')
    .option('--tags <ids>', 'Comma-separated tag IDs')
    .option('--docs <ids>', 'Comma-separated document IDs')
    .action((opts: { tags?: string; docs?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveWorkspace()

        let tagIds: string[]
        if (opts.tags) {
          tagIds = opts.tags.split(',').map((s) => s.trim())
        } else {
          const data = await tagOps.listTags(arbi)
          if (data.length === 0) {
            console.log('No tags found.')
            return
          }
          tagIds = await promptCheckbox(
            'Select tags to generate',
            data.map((t) => ({
              name: `${t.name} (${(t.tag_type as { type?: string })?.type ?? ''})`,
              value: t.external_id as string,
            }))
          )
          if (tagIds.length === 0) return
        }

        let docIds: string[]
        if (opts.docs) {
          docIds = opts.docs.split(',').map((s) => s.trim())
        } else {
          docIds = await pickDocs(arbi, 'Select documents for AI tagging')
          if (docIds.length === 0) return
        }

        const data = await doctagOps.generateDocTags(arbi, tagIds, docIds)
        console.log(
          `Generating doctags for ${data.doc_ext_ids.length} doc(s) with ${data.tag_ext_ids.length} tag(s).`
        )
      })()
    )
}
